import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Loader2, ToggleRight, Save, AlertTriangle, Undo2 } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";

type Feature = { key: string; name: string; description: string; enabled: boolean; tiers: string[]; config?: { url?: string } };
const same = (a: Feature, b: Feature) =>
  a.enabled === b.enabled &&
  [...a.tiers].sort().join() === [...b.tiers].sort().join() &&
  (a.config?.url || "") === (b.config?.url || "");

export default function AdminFeatures() {
  usePageTitle("Secciones del portal");
  const [features, setFeatures] = useState<Feature[]>([]);
  const [saved, setSaved] = useState<Record<string, Feature>>({});
  const [tierNames, setTierNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState<Feature | null>(null);

  const load = () => {
    setFailed(false);
    return Promise.all([api.get("/admin/features"), api.get("/tiers")])
      .then(([f, t]) => {
        setFeatures(f.data);
        setSaved(Object.fromEntries(f.data.map((x: Feature) => [x.key, x])));
        setTierNames(t.data.map((x: any) => x.name));
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const dirty = features.filter((f) => saved[f.key] && !same(f, saved[f.key]));
  // Avisar al salir de la página con cambios sin guardar.
  useEffect(() => {
    if (!dirty.length) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty.length]);

  const patch = (key: string, p: Partial<Feature>) => setFeatures((prev) => prev.map((f) => (f.key === key ? { ...f, ...p } : f)));
  const toggleTier = (f: Feature, tier: string) =>
    patch(f.key, { tiers: f.tiers.includes(tier) ? f.tiers.filter((t) => t !== tier) : [...f.tiers, tier] });
  const setConfigUrl = (key: string, url: string) => patch(key, { config: { url } });

  const persist = async (f: Feature) => {
    setSavingKey(f.key);
    try {
      await api.put(`/admin/features/${f.key}`, { enabled: f.enabled, tiers: f.tiers, config: f.config || {} });
      setSaved((prev) => ({ ...prev, [f.key]: f }));
      toast.success(f.enabled ? `“${f.name}” guardado` : `“${f.name}” quedó apagado para todos los clientes`);
    } catch (err: any) {
      toast.error(apiError(err));
      throw err;
    } finally {
      setSavingKey(null);
    }
  };

  const save = (f: Feature) => {
    // Apagar una sección afecta a todos los clientes: se confirma antes.
    if (!f.enabled && saved[f.key]?.enabled) setConfirmOff(f);
    else persist(f).catch(() => {});
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-features-page">
      <div>
        <h1 className="text-3xl font-serif font-medium">Secciones del portal</h1>
        <p className="text-sm text-slate-600 mt-1">
          Elige qué partes del portal ve cada nivel de clientes. Los cambios se aplican al pulsar “Guardar” en cada tarjeta.
        </p>
      </div>

      {dirty.length > 0 && (
        <div role="status" data-testid="features-unsaved" className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
          Tienes cambios sin guardar en: {dirty.map((f) => f.name).join(", ")}.
        </div>
      )}

      {failed ? (
        <LoadError what="las secciones del portal" onRetry={load} />
      ) : (
        <div className="grid gap-5">
          {features.map((f) => {
            const isDirty = saved[f.key] && !same(f, saved[f.key]);
            const noTiers = f.enabled && f.tiers.length === 0;
            return (
              <section key={f.key} data-testid={`feature-row-${f.key}`} aria-labelledby={`feat-${f.key}`} className={`card-obsidian p-6 ${isDirty ? "ring-2 ring-amber-400" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
                      <ToggleRight className="w-5 h-5 text-gold-ink" aria-hidden="true" />
                    </div>
                    <div>
                      <h2 id={`feat-${f.key}`} className="text-lg font-semibold text-slate-900">{f.name}</h2>
                      <p className="text-sm text-slate-600">{f.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor={`feature-enabled-${f.key}`} className="text-sm text-slate-700">{f.enabled ? "Encendida" : "Apagada"}</label>
                    <Switch id={`feature-enabled-${f.key}`} data-testid={`feature-enabled-${f.key}`} checked={f.enabled} onCheckedChange={(v: boolean) => patch(f.key, { enabled: v })} />
                  </div>
                </div>

                <fieldset className={`mt-5 ${f.enabled ? "" : "opacity-50"}`} disabled={!f.enabled}>
                  <legend className="text-sm font-medium text-slate-700 mb-3">Niveles que la ven</legend>
                  <div className="flex flex-wrap gap-4">
                    {tierNames.map((t) => (
                      <label key={t} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          data-testid={`feature-${f.key}-tier-${t}`}
                          checked={f.tiers.includes(t)}
                          onCheckedChange={() => toggleTier(f, t)}
                        />
                        <span className="text-sm text-slate-700">{t}</span>
                      </label>
                    ))}
                  </div>
                  {noTiers && (
                    <p className="text-sm text-amber-800 mt-3 flex items-center gap-1.5" role="alert">
                      <AlertTriangle className="w-4 h-4" aria-hidden="true" /> Está encendida pero ningún nivel la ve: en la práctica nadie tendrá acceso.
                    </p>
                  )}

                  {(f.key === "commercial_advisor" || f.key === "technical_support") && (
                    <div className="mt-4">
                      <label htmlFor={`feature-${f.key}-url`} className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2 block">
                        {f.key === "commercial_advisor" ? "Enlace del asesor comercial (ej. WhatsApp)" : "Enlace de soporte técnico (ej. WhatsApp)"}
                      </label>
                      <Input
                        id={`feature-${f.key}-url`}
                        data-testid={`feature-${f.key}-url`}
                        placeholder="https://wa.me/..."
                        value={f.config?.url || ""}
                        onChange={(e: any) => setConfigUrl(f.key, e.target.value)}
                      />
                    </div>
                  )}
                </fieldset>

                <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
                  {isDirty && (
                    <>
                      <span className="text-sm text-amber-800">Cambios sin guardar</span>
                      <Button variant="ghost" onClick={() => patch(f.key, saved[f.key])} className="rounded-full text-slate-700">
                        <Undo2 className="w-4 h-4 mr-1" aria-hidden="true" /> Descartar
                      </Button>
                    </>
                  )}
                  <Button
                    data-testid={`feature-save-${f.key}`}
                    onClick={() => save(f)}
                    disabled={savingKey === f.key || !isDirty}
                    className="gold-gradient-bg text-white font-semibold rounded-full px-5"
                  >
                    {savingKey === f.key ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : <><Save className="w-4 h-4 mr-1.5" aria-hidden="true" /> Guardar</>}
                  </Button>
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmOff}
        onOpenChange={(o) => !o && setConfirmOff(null)}
        danger
        title={`¿Apagar “${confirmOff?.name || ""}”?`}
        description={<p>Ningún cliente, de ningún nivel, podrá usar esta sección hasta que la vuelvas a encender. Verán un aviso de que no está disponible.</p>}
        confirmLabel="Apagar para todos"
        onConfirm={() => persist(confirmOff!)}
      />
    </div>
  );
}
